import type { CSSProperties } from 'react';
import type { BoardBackground, BoardBackgroundPut } from '@molt/shared';
import coastDaylightUrl from '@/assets/board-backgrounds/coast-daylight.png';
import forestDuskUrl from '@/assets/board-backgrounds/forest-dusk.png';
import paperLightUrl from '@/assets/board-backgrounds/paper-light.png';

export interface BoardBackgroundSwatch {
  id: string;
  name: string;
  labelKey: string;
  textTreatment: BoardBackgroundPut['textTreatment'];
  cssVar: string;
  style: CSSProperties;
}

export interface BoardBackgroundPhoto {
  id: string;
  name: string;
  labelKey: string;
  photoRef: string;
  /**
   * ⚠ THE WIRE VALUE, AND IT MUST NOT BE TRANSLATED. FE-80.1.
   *
   * `photoBackgroundPut()` ships this as the B42 `photoAttribution`, where it
   * lands in `boards.background_photo_attribution varchar(512)`;
   * `card-cover.ts`'s `coverPhotoOptions()` re-exports it and
   * `CardCoverSection` sends it as a card cover's `photoAttribution`; and
   * `e2e/FR-EXP-022.spec.ts` pins these exact bytes on the board export.
   * Routing this field would make the value a board STORES depend on the
   * language of whoever happened to pick the background.
   *
   * Render {@link attributionKey} instead. This is the same split `name` and
   * `labelKey` already make one field above.
   */
  attribution: string;
  /**
   * The catalogue key for the sentence a reader sees. Twin of
   * {@link attribution}, exactly as `labelKey` is the twin of `name`.
   *
   * Written out as a literal at each call site rather than derived from
   * `labelKey`, so that grepping the key finds the photo that declares it.
   */
  attributionKey: string;
  textTreatment: BoardBackgroundPut['textTreatment'];
  fallbackCssVar: string;
  imageUrl: string;
  style: CSSProperties;
}

function swatch(
  id: string,
  name: string,
  labelKey: string,
  cssVar: string,
  textTreatment: BoardBackgroundPut['textTreatment'],
): BoardBackgroundSwatch {
  return {
    id,
    name,
    labelKey,
    cssVar,
    textTreatment,
    style: { backgroundColor: `var(${cssVar})` },
  };
}

function photo(
  id: string,
  name: string,
  labelKey: string,
  attribution: string,
  attributionKey: string,
  imageUrl: string,
  fallbackCssVar: string,
  textTreatment: BoardBackgroundPut['textTreatment'],
): BoardBackgroundPhoto {
  return {
    id,
    name,
    labelKey,
    photoRef: `molt-stock:${id}`,
    attribution,
    attributionKey,
    imageUrl,
    fallbackCssVar,
    textTreatment,
    style: {
      backgroundColor: `var(${fallbackCssVar})`,
      backgroundImage: `linear-gradient(rgb(0 0 0 / 0.08), rgb(0 0 0 / 0.08)), url(${imageUrl})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    },
  };
}

export const BOARD_BACKGROUND_SWATCHES: readonly BoardBackgroundSwatch[] = [
  swatch(
    'paper',
    'Paper',
    'primitives.boardBackground.swatch.paper',
    '--board-bg-paper',
    'dark',
  ),
  swatch(
    'sage',
    'Sage',
    'primitives.boardBackground.swatch.sage',
    '--board-bg-sage',
    'dark',
  ),
  swatch('sky', 'Sky', 'primitives.boardBackground.swatch.sky', '--board-bg-sky', 'dark'),
  swatch(
    'clay',
    'Clay',
    'primitives.boardBackground.swatch.clay',
    '--board-bg-clay',
    'dark',
  ),
  swatch(
    'berry',
    'Berry',
    'primitives.boardBackground.swatch.berry',
    '--board-bg-berry',
    'light',
  ),
  swatch(
    'pine',
    'Pine',
    'primitives.boardBackground.swatch.pine',
    '--board-bg-pine',
    'light',
  ),
  swatch(
    'indigo',
    'Indigo',
    'primitives.boardBackground.swatch.indigo',
    '--board-bg-indigo',
    'light',
  ),
  swatch(
    'charcoal',
    'Charcoal',
    'primitives.boardBackground.swatch.charcoal',
    '--board-bg-charcoal',
    'light',
  ),
];

export const BOARD_BACKGROUND_PHOTOS: readonly BoardBackgroundPhoto[] = [
  photo(
    'forest-dusk',
    'Forest dusk',
    'primitives.boardBackground.photo.forestDusk',
    'Generated stock photo: forest hills at dusk',
    'primitives.boardBackground.photo.forestDusk.attribution',
    forestDuskUrl,
    '--board-bg-pine',
    'light',
  ),
  photo(
    'coast-daylight',
    'Coast daylight',
    'primitives.boardBackground.photo.coastDaylight',
    'Generated stock photo: bright coast',
    'primitives.boardBackground.photo.coastDaylight.attribution',
    coastDaylightUrl,
    '--board-bg-sky',
    'dark',
  ),
  photo(
    'paper-light',
    'Paper light',
    'primitives.boardBackground.photo.paperLight',
    'Generated stock photo: warm paper texture',
    'primitives.boardBackground.photo.paperLight.attribution',
    paperLightUrl,
    '--board-bg-paper',
    'dark',
  ),
];

const SWATCH_BY_ID = new Map(BOARD_BACKGROUND_SWATCHES.map((entry) => [entry.id, entry]));
const PHOTO_BY_REF = new Map(
  BOARD_BACKGROUND_PHOTOS.map((entry) => [entry.photoRef, entry]),
);

export type BoardBackgroundTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export function boardBackgroundPresetLabel(
  t: BoardBackgroundTranslate,
  preset: Pick<BoardBackgroundSwatch | BoardBackgroundPhoto, 'labelKey' | 'name'>,
): string {
  return t(preset.labelKey);
}

/**
 * FE-80.1 — the reader-facing half of a stock photo's attribution.
 *
 * ⚠ The twin of {@link boardBackgroundPresetLabel}, and it exists for the same
 * reason. `BoardBackgroundPicker` used to print `photo.attribution` — the
 * value that goes on the WIRE — straight at a human, one line under a name
 * that was already routed. So the picker rendered
 * `Bosque al anochecer` / `Generated stock photo: forest hills at dusk`: half
 * translated, half not, on two adjacent lines of one button.
 *
 * `attribution` is untouched by this and must stay untouched: it is what
 * `photoBackgroundPut()` stores. Only what is SHOWN resolves through here.
 */
export function boardBackgroundPhotoAttribution(
  t: BoardBackgroundTranslate,
  preset: Pick<BoardBackgroundPhoto, 'attributionKey' | 'attribution'>,
): string {
  return t(preset.attributionKey);
}

export function isChosenBoardBackground(
  background: BoardBackground | null | undefined,
): boolean {
  if (background?.type === 'colour') {
    return background.colour !== null && SWATCH_BY_ID.has(background.colour);
  }

  if (background?.type === 'photo') {
    return background.photoRef !== null && PHOTO_BY_REF.has(background.photoRef);
  }

  if (background?.type === 'custom_upload') {
    return background.backgroundCustomId !== null;
  }

  return false;
}

export function boardBackgroundStyle(
  background: BoardBackground | null | undefined,
  boardId?: string,
): CSSProperties | undefined {
  if (!isChosenBoardBackground(background)) return undefined;

  if (background?.type === 'colour') {
    return SWATCH_BY_ID.get(background.colour ?? '')?.style;
  }

  if (background?.type === 'photo') {
    return PHOTO_BY_REF.get(background.photoRef ?? '')?.style;
  }

  if (background?.type === 'custom_upload' && background.backgroundCustomId !== null) {
    if (boardId === undefined) return { backgroundColor: 'var(--color-board)' };
    const url = customBoardBackgroundDownloadUrl(boardId, background.backgroundCustomId);
    return {
      backgroundColor: 'var(--color-board)',
      backgroundImage: `url(${url})`,
      backgroundPosition: 'center',
      backgroundRepeat: background.renderMode === 'tiled' ? 'repeat' : 'no-repeat',
      backgroundSize: background.renderMode === 'tiled' ? 'auto' : 'cover',
    };
  }

  return undefined;
}

export function boardBackgroundLabel(
  t: BoardBackgroundTranslate,
  background: BoardBackground | null | undefined,
): string {
  if (background === null || background === undefined)
    return t('primitives.boardBackground.default');
  if (background?.type === 'colour') {
    if (background.colour === null) return t('primitives.boardBackground.default');
    const swatch = SWATCH_BY_ID.get(background.colour ?? '');
    return swatch === undefined
      ? t('primitives.boardBackground.customColour')
      : boardBackgroundPresetLabel(t, swatch);
  }
  if (background?.type === 'photo') {
    const photo = PHOTO_BY_REF.get(background.photoRef ?? '');
    return photo === undefined
      ? t('primitives.boardBackground.stockPhoto')
      : boardBackgroundPresetLabel(t, photo);
  }
  return t('primitives.boardBackground.customUpload');
}

export function customBoardBackgroundDownloadUrl(
  boardId: string,
  backgroundId: string,
): string {
  return `/api/boards/${encodeURIComponent(boardId)}/background/custom/${encodeURIComponent(
    backgroundId,
  )}/download`;
}

export function colourBackgroundPut(id: string): BoardBackgroundPut {
  const swatch = SWATCH_BY_ID.get(id);
  return {
    type: 'colour',
    colour: id,
    renderMode: 'full_cover',
    textTreatment: swatch?.textTreatment ?? 'dark',
  };
}

export function photoBackgroundPut(photoRef: string): BoardBackgroundPut {
  const preset = PHOTO_BY_REF.get(photoRef);
  return {
    type: 'photo',
    photoRef,
    photoAttribution: preset?.attribution ?? null,
    renderMode: 'full_cover',
    textTreatment: preset?.textTreatment ?? 'light',
  };
}

export function filterBoardBackgroundPhotos(
  t: BoardBackgroundTranslate,
  query: string,
): readonly BoardBackgroundPhoto[] {
  const normalised = query.trim().toLowerCase();
  if (normalised === '') return BOARD_BACKGROUND_PHOTOS;
  return BOARD_BACKGROUND_PHOTOS.filter((entry) =>
    `${boardBackgroundPresetLabel(t, entry)} ${boardBackgroundPhotoAttribution(t, entry)}`
      .toLowerCase()
      .includes(normalised),
  );
}
